/**
 * Resolves in-app navigation targets for notification clicks using dynamic menu
 * permissions (session menu cache). Customer users are scoped to portal routes only.
 */

import { getCurrentUser } from '../api/authService';
import { isCustomer, isTeamLead } from './roleUtils';

const LOG_NS = 'notification-navigation';

const CANONICAL_AGENT_UNASSIGNED = '/agent/tickets/unassigned';
const CANONICAL_AGENT_TEAM_TICKETS = '/agent/tickets/team-tickets';
const CANONICAL_PORTAL_TICKETS = '/portal/tickets';
const TICKET_NUMBER_PATTERN = /#?\s*(TKT-\d+)/i;
const NOTIFICATION_TEAM_TICKETS_TAB_KEY = 'notificationTeamTicketsTab';
const NOTIFICATION_CUSTOMER_OPEN_TICKET_KEY = 'notificationCustomerOpenTicket';
export const CUSTOMER_PORTAL_TICKET_NOTIFICATION_EVENT = 'hd-notification-portal-ticket';

/** @type {{ navigate: ((path: string) => void) | null, markAsRead: ((notification: object) => void) | null }} */
let notificationClickActions = { navigate: null, markAsRead: null };

function logNav(payload) {
  //console.log(JSON.stringify({ ns: LOG_NS, ts: new Date().toISOString(), ...payload }));
  //console.log('🔍 Notification path:', payload);
}

function normalizeRoute(route) {
  if (!route || typeof route !== 'string') return '';
  return route.replace(/\/+$/, '') || '/';
}

/**
 * Read and clear ticket search token written by notification navigation.
 * @returns {string | null}
 */
export function consumeNotificationTicketSearch() {
  const raw = sessionStorage.getItem('notificationTicketNumber');
  if (!raw) {
    return null;
  }
  sessionStorage.removeItem('notificationTicketNumber');
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null || parsed === '') {
      return null;
    }
    return String(parsed).trim() || null;
  } catch {
    return String(raw).trim() || null;
  }
}

export function patchNotificationTicketSearch(ticketNumber) {
  if (ticketNumber == null || String(ticketNumber).trim() === '') {
    return;
  }
  sessionStorage.setItem('notificationTicketNumber', JSON.stringify(String(ticketNumber).trim()));
}

/**
 * Queue customer portal ticket-details open after navigation (consumed by portal My Tickets).
 * @param {{ ticketNumber?: string | null, ticketId?: string | number | null }} payload
 */
export function patchNotificationCustomerTicketOpen({ ticketNumber = null, ticketId = null } = {}) {
  const number = ticketNumber != null ? String(ticketNumber).trim() : '';
  const id = ticketId != null ? String(ticketId).trim() : '';
  if (!number && !id) {
    return;
  }
  sessionStorage.setItem(
    NOTIFICATION_CUSTOMER_OPEN_TICKET_KEY,
    JSON.stringify({
      ticketNumber: number || null,
      ticketId: id || null,
    })
  );
}

/**
 * @returns {{ ticketNumber: string | null, ticketId: string | null } | null}
 */
export function consumeNotificationCustomerTicketOpen() {
  const raw = sessionStorage.getItem(NOTIFICATION_CUSTOMER_OPEN_TICKET_KEY);
  if (!raw) {
    return null;
  }
  sessionStorage.removeItem(NOTIFICATION_CUSTOMER_OPEN_TICKET_KEY);
  try {
    const parsed = JSON.parse(raw);
    const ticketNumber = parsed?.ticketNumber ? String(parsed.ticketNumber).trim() : null;
    const ticketId = parsed?.ticketId ? String(parsed.ticketId).trim() : null;
    if (!ticketNumber && !ticketId) {
      return null;
    }
    return { ticketNumber, ticketId };
  } catch {
    return null;
  }
}

function extractTicketIdFromNotification(notification) {
  if (!notification) {
    return null;
  }
  const meta = notification.metadata || notification.data || {};
  const direct =
    notification.ticketId ??
    meta.ticketId ??
    notification.data?.ticketId ??
    null;
  if (direct == null || String(direct).trim() === '') {
    return null;
  }
  return String(direct).trim();
}

/** @param {'team-tickets' | 'pending-assignments'} tab */
export function patchNotificationTeamTicketsTab(tab) {
  if (tab !== 'team-tickets' && tab !== 'pending-assignments') {
    return;
  }
  sessionStorage.setItem(NOTIFICATION_TEAM_TICKETS_TAB_KEY, tab);
}

/**
 * @returns {'team-tickets' | 'pending-assignments' | null}
 */
export function consumeNotificationTeamTicketsTab() {
  const tab = sessionStorage.getItem(NOTIFICATION_TEAM_TICKETS_TAB_KEY);
  sessionStorage.removeItem(NOTIFICATION_TEAM_TICKETS_TAB_KEY);
  if (tab === 'team-tickets' || tab === 'pending-assignments') {
    return tab;
  }
  return null;
}

export function isCurrentUserCustomer() {
  const user = getCurrentUser();
  if (user) {
    if (user.roleCode === 'CUSTOMER') {
      return true;
    }
    if (isCustomer(user.role)) {
      return true;
    }
  }
  if (typeof window !== 'undefined') {
    return window.location.pathname.startsWith('/portal');
  }
  return false;
}

export function isCurrentUserTeamLead() {
  const user = getCurrentUser();
  if (!user) {
    return false;
  }
  if (user.roleCode === 'TEAM_LEAD') {
    return true;
  }
  return isTeamLead(user.role);
}

function isAgentOnlyRoute(route) {
  const normalized = normalizeRoute(route).toLowerCase();
  return normalized === '/agent' || normalized.startsWith('/agent/');
}

function isAgentTicketDetailRoute(route) {
  return /^\/agent\/tickets\/\d+$/i.test(normalizeRoute(route));
}

function isTeamLeadPendingAssignmentsNotificationType(nt) {
  return (
    nt === 'ticket_team_transferred' ||
    nt === 'team_transferred' ||
    nt === 'ticket_escalated_to_team_leads'
  );
}

/**
 * @param {object} notification
 * @returns {string | null}
 */
export function extractTicketNumberFromNotification(notification) {
  if (!notification) {
    return null;
  }
  const meta = notification.metadata || notification.data || {};
  const direct =
    notification.ticketNumber ??
    meta.ticketNumber ??
    notification.data?.ticketNumber ??
    null;
  if (direct != null && String(direct).trim() !== '') {
    return String(direct).trim();
  }
  const text = String(notification.message || notification.body || notification.title || '');
  const match = text.match(TICKET_NUMBER_PATTERN);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Portal "My Tickets" screen from dynamic menu (when available).
 * @param {Array<object>} menuData
 * @returns {object | null}
 */
export function findPortalTicketsMenuScreen(menuData) {
  if (!Array.isArray(menuData)) {
    return null;
  }
  for (const module of menuData) {
    if (module.isActive !== true) continue;
    for (const screen of module.submenu || []) {
      if (screen.isActive !== true || screen.canView !== true || !screen.routing) {
        continue;
      }
      const route = normalizeRoute(screen.routing).toLowerCase();
      const label = String(
        screen.screenLable || screen.screenLabel || screen.screenName || ''
      ).toLowerCase();
      if (route === CANONICAL_PORTAL_TICKETS.toLowerCase()) {
        return screen;
      }
      if (route.endsWith('/portal/tickets') && !route.includes('/create')) {
        return screen;
      }
      if (
        (label.includes('customer') && label.includes('ticket')) ||
        label.includes('my tickets')
      ) {
        return screen;
      }
    }
  }
  return null;
}

/**
 * Customer-safe ticket list navigation with search patch (same pattern as unassigned queue).
 * @param {object} notification
 * @param {Array<object>} menuData
 * @returns {{ path: string, decision: string, targetRoute: string }}
 */
/**
 * Team lead queue navigation with tab + search patch (same pattern as unassigned queue).
 * @param {object} notification
 * @param {Array<object>} menuData
 * @param {'team-tickets' | 'pending-assignments'} tab
 */
function resolveTeamLeadTeamTicketsNavigation(notification, menuData, tab) {
  const ticketNumber = extractTicketNumberFromNotification(notification);
  patchNotificationTicketSearch(ticketNumber);
  patchNotificationTeamTicketsTab(tab);
  const teamScreen = findTeamTicketsMenuScreen(menuData);
  const target = teamScreen?.routing
    ? normalizeRoute(teamScreen.routing)
    : CANONICAL_AGENT_TEAM_TICKETS;
  logNav({
    decision: `team_lead_${tab}_search`,
    permissionResult: teamScreen ? 'granted' : 'default_route',
    selectedTargetRoute: target,
    ticketNumber,
    tab,
  });
  return {
    path: target,
    decision: `team_lead_${tab}_search`,
    targetRoute: target,
  };
}

function resolveTeamLeadTicketQueueNavigation(notification, menuData, nt) {
  const tab = isTeamLeadPendingAssignmentsNotificationType(nt)
    ? 'pending-assignments'
    : 'team-tickets';
  return resolveTeamLeadTeamTicketsNavigation(notification, menuData, tab);
}

function resolveCustomerPortalTicketsNavigation(notification, menuData) {
  const ticketNumber = extractTicketNumberFromNotification(notification);
  const ticketId = extractTicketIdFromNotification(notification);
  patchNotificationCustomerTicketOpen({ ticketNumber, ticketId });
  const portalScreen = findPortalTicketsMenuScreen(menuData);
  const target = portalScreen?.routing
    ? normalizeRoute(portalScreen.routing)
    : CANONICAL_PORTAL_TICKETS;
  logNav({
    decision: 'customer_portal_tickets_open',
    permissionResult: portalScreen ? 'granted' : 'default_route',
    selectedTargetRoute: target,
    ticketNumber,
    ticketId,
  });
  return {
    path: target,
    decision: 'customer_portal_tickets_open',
    targetRoute: target,
  };
}

/**
 * @param {unknown} menuDataProp
 * @returns {Array<object>}
 */
export function loadMenuDataForNavigation(menuDataProp) {
  if (Array.isArray(menuDataProp) && menuDataProp.length > 0) {
    return menuDataProp;
  }
  try {
    const raw = sessionStorage.getItem('hdDynamicMenuCache');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function defaultDashboardPathByLocation(pathname) {
  if (!pathname || typeof pathname !== 'string') {
    return '/agent/dashboard';
  }
  if (pathname.startsWith('/admin')) return '/admin/dashboard';
  if (pathname.startsWith('/super-admin')) return '/super-admin/dashboard';
  if (pathname.startsWith('/portal')) return '/portal/dashboard';
  if (pathname.startsWith('/developerdashboard') || pathname.startsWith('/integration-dashboard')) {
    return '/developerdashboard';
  }
  return '/agent/dashboard';
}

/**
 * Dashboard route from menu (isDashboard + canView). Never returns a random first screen.
 * @param {Array<object>} menuData
 * @param {string} pathname
 * @returns {string}
 */
export function resolveDashboardFallbackRoute(menuData, pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  for (const module of menuData) {
    if (module.isActive !== true) continue;
    for (const screen of module.submenu || []) {
      if (
        screen.isActive === true &&
        screen.canView === true &&
        screen.isDashboard === true &&
        screen.routing
      ) {
        return normalizeRoute(screen.routing);
      }
    }
  }
  return defaultDashboardPathByLocation(pathname);
}

/**
 * Find Unassigned Tickets screen using API menu shape (routing + labels + canView).
 * @param {Array<object>} menuData
 * @returns {object | null}
 */
/**
 * Team Tickets screen from dynamic menu (team lead workflow).
 * @param {Array<object>} menuData
 * @returns {object | null}
 */
export function findTeamTicketsMenuScreen(menuData) {
  if (!Array.isArray(menuData)) {
    return null;
  }
  for (const module of menuData) {
    if (module.isActive !== true) continue;
    for (const screen of module.submenu || []) {
      if (screen.isActive !== true || screen.canView !== true || !screen.routing) {
        continue;
      }
      const route = normalizeRoute(screen.routing).toLowerCase();
      const label = String(
        screen.screenLable || screen.screenLabel || screen.screenName || ''
      ).toLowerCase();
      if (route === CANONICAL_AGENT_TEAM_TICKETS.toLowerCase()) {
        return screen;
      }
      if (route.endsWith('/tickets/team-tickets')) {
        return screen;
      }
      if (label.includes('team') && label.includes('ticket')) {
        return screen;
      }
    }
  }
  return null;
}

export function findUnassignedTicketsMenuScreen(menuData) {
  if (!Array.isArray(menuData)) {
    return null;
  }
  for (const module of menuData) {
    if (module.isActive !== true) continue;
    for (const screen of module.submenu || []) {
      if (screen.isActive !== true || screen.canView !== true || !screen.routing) {
        continue;
      }
      const route = normalizeRoute(screen.routing).toLowerCase();
      const label = String(
        screen.screenLable || screen.screenLabel || screen.screenName || ''
      ).toLowerCase();

      if (route === CANONICAL_AGENT_UNASSIGNED.toLowerCase()) {
        return screen;
      }
      if (route.endsWith('/tickets/unassigned')) {
        return screen;
      }
      if (label.includes('unassigned') && label.includes('ticket')) {
        return screen;
      }
    }
  }
  return null;
}

function hasConcreteAssigneeFromMetadata(meta) {
  if (!meta || typeof meta !== 'object') {
    return false;
  }
  const candidates = [
    meta.destinationAssigneeId,
    meta.assigneeId,
    meta.assignedAgentId,
    meta.destination_assignee_id,
    meta.assigned_agent_id,
  ];
  for (const v of candidates) {
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : Number(String(v).trim());
    if (Number.isFinite(n) && n > 0) {
      return true;
    }
  }
  return false;
}

/**
 * True for new unassigned pool tickets only (not assigned / transfer / escalation flows).
 * @param {object} notification
 * @returns {boolean}
 */
export function isUnassignedTicketCreatedNotification(notification) {
  if (!notification) return false;
  const nt = String(notification.notificationType || notification.type || '').toLowerCase();
  if (nt === 'new_unassigned_ticket') {
    return true;
  }
  if (nt !== 'ticket_created') {
    return false;
  }
  const meta = notification.metadata || notification.data || {};
  if (hasConcreteAssigneeFromMetadata(meta)) {
    return false;
  }
  const bridge = meta.bridgeEventType;
  if (bridge && String(bridge) !== 'TICKET_CREATED') {
    return false;
  }
  const msg = String(notification.message || notification.body || '').toLowerCase();
  if (msg.includes('assigned to you') || msg.includes('reassigned')) {
    return false;
  }
  return true;
}

/**
 * @param {object} notification
 * @param {unknown} menuDataProp
 * @returns {{ path: string, decision: string, permissionResult?: string, targetRoute?: string, fallbackReason?: string }}
 */
export function resolveNotificationClickNavigation(notification, menuDataProp) {
  const menuData = loadMenuDataForNavigation(menuDataProp);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const customerUser = isCurrentUserCustomer();
  const teamLeadUser = isCurrentUserTeamLead();

  const ticketId =
    notification.ticketId ??
    notification.metadata?.ticketId ??
    notification.data?.ticketId ??
    null;

  const nt = String(notification.notificationType || notification.type || '').toLowerCase();

  if (customerUser && nt === 'chat_request') {
    return resolveCustomerPortalTicketsNavigation(notification, menuData);
  }

  if (
    customerUser &&
    (nt === 'ticket_reply' ||
      nt === 'customer_ticket_reply' ||
      nt === 'ticket_assigned')
  ) {
    return resolveCustomerPortalTicketsNavigation(notification, menuData);
  }

  if (nt === 'chat_request') {
    const chatSessionId =
      notification.metadata?.chatSessionId ||
      notification.data?.chatSessionId ||
      notification.chatSessionId;
    if (chatSessionId) {
      const path = `/agent/chat/${chatSessionId}`;
      logNav({
        decision: 'chat_request',
        permissionResult: 'n_a',
        selectedTargetRoute: path,
      });
      return { path, decision: 'chat_request' };
    }
    logNav({
      decision: 'chat_request_no_session',
      permissionResult: 'n_a',
      selectedTargetRoute: null,
    });
    return { path: null, decision: 'chat_request_no_session' };
  }
  // chat_accepted and other types: continue to link / ticket / special rules below

  if (teamLeadUser && !customerUser) {
    if (isTeamLeadPendingAssignmentsNotificationType(nt)) {
      return resolveTeamLeadTeamTicketsNavigation(notification, menuData, 'pending-assignments');
    }
    if (nt === 'ticket_assigned') {
      return resolveTeamLeadTeamTicketsNavigation(notification, menuData, 'team-tickets');
    }
  }

  if (isUnassignedTicketCreatedNotification(notification)) {
    if (customerUser) {
      return resolveCustomerPortalTicketsNavigation(notification, menuData);
    }
    const unassignedScreen = findUnassignedTicketsMenuScreen(menuData);
    const menuResolved = menuData.length > 0;

    if (unassignedScreen?.routing) {
      const target = normalizeRoute(unassignedScreen.routing);
      patchNotificationTicketSearch(
        extractTicketNumberFromNotification(notification) ?? notification.ticketNumber
      );
      logNav({
        decision: 'unassigned_ticket_created',
        permissionResult: 'granted',
        selectedTargetRoute: target,
        menuScreenName: unassignedScreen.screenName,
        ticketId,
      });
      return {
        path: target,
        decision: 'unassigned_ticket_created',
        permissionResult: 'granted',
        targetRoute: target,
      };
    }

    if (!menuResolved) {
      const fallback = resolveDashboardFallbackRoute(menuData, pathname);
      logNav({
        decision: 'unassigned_ticket_created',
        permissionResult: 'indeterminate',
        fallbackReason: 'menu_cache_empty',
        selectedTargetRoute: fallback,
        ticketId,
      });
      return {
        path: fallback,
        decision: 'fallback_dashboard_menu_unavailable',
        permissionResult: 'indeterminate',
        fallbackReason: 'menu_cache_empty',
        targetRoute: fallback,
      };
    }

    const fallback = resolveDashboardFallbackRoute(menuData, pathname);
    logNav({
      decision: 'unassigned_ticket_created',
      permissionResult: 'denied',
      fallbackReason: 'no_unassigned_tickets_screen_in_menu',
      selectedTargetRoute: fallback,
      ticketId,
    });
    return {
      path: fallback,
      decision: 'fallback_dashboard_no_unassigned_permission',
      permissionResult: 'denied',
      fallbackReason: 'no_unassigned_tickets_screen_in_menu',
      targetRoute: fallback,
    };
  }

  if (notification.link && String(notification.link).trim() !== '') {
    let link = String(notification.link).trim();
    if (!link.startsWith('/')) {
      link = `/${link}`;
    }
    const target = normalizeRoute(link);
    if (
      customerUser &&
      (isAgentOnlyRoute(target) || /\/portal\/tickets\/\d+/i.test(target))
    ) {
      return resolveCustomerPortalTicketsNavigation(notification, menuData);
    }
    if (teamLeadUser && !customerUser && isAgentTicketDetailRoute(target)) {
      return resolveTeamLeadTicketQueueNavigation(notification, menuData, nt);
    }
    logNav({
      decision: 'follow_notification_link',
      permissionResult: 'n_a',
      selectedTargetRoute: target,
      ticketId,
    });
    return { path: target, decision: 'follow_notification_link', targetRoute: target };
  }

  if (ticketId != null && String(ticketId).trim() !== '') {
    if (customerUser) {
      return resolveCustomerPortalTicketsNavigation(notification, menuData);
    }
    if (teamLeadUser) {
      return resolveTeamLeadTicketQueueNavigation(notification, menuData, nt);
    }
    const target = `/agent/tickets/${ticketId}`;
    logNav({
      decision: 'ticket_detail_by_id',
      permissionResult: 'n_a',
      selectedTargetRoute: target,
      ticketId,
    });
    return { path: target, decision: 'ticket_detail_by_id', targetRoute: target };
  }

  logNav({
    decision: 'no_navigation_target',
    permissionResult: 'n_a',
    selectedTargetRoute: null,
  });
  return { path: null, decision: 'no_navigation_target' };
}

const SYNTHETIC_NOTIFICATION_ID_PREFIXES = ['qr:', 'local:', 'fcm-local:', 'temp-'];

/**
 * True only for numeric ids persisted in the backend notifications table.
 * Rejects synthetic queue ids (qr:*), local UI ids, and Date.now() fallbacks.
 * @param {unknown} notificationId
 * @returns {boolean}
 */
export function isBackendPersistedNotificationId(notificationId) {
  if (notificationId == null || notificationId === '') {
    return false;
  }
  const raw = String(notificationId).trim();
  if (!raw) {
    return false;
  }
  const lower = raw.toLowerCase();
  if (SYNTHETIC_NOTIFICATION_ID_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return false;
  }
  if (!/^\d+$/.test(raw)) {
    return false;
  }
  const num = Number(raw);
  if (!Number.isFinite(num) || num <= 0 || !Number.isInteger(num)) {
    return false;
  }
  // Millisecond timestamps (Date.now() fallbacks) — not DB notification ids
  if (raw.length >= 13 || num >= 1_000_000_000_000) {
    return false;
  }
  return true;
}

function resolvePersistedNotificationId(...candidates) {
  for (const candidate of candidates) {
    if (isBackendPersistedNotificationId(candidate)) {
      return candidate;
    }
  }
  return null;
}

/**
 * Normalize WS/FCM/bell payloads into the shape expected by resolveNotificationClickNavigation.
 * @param {object} source
 * @returns {object | null}
 */
export function buildNotificationNavigationPayload(source) {
  if (!source || typeof source !== 'object') {
    return null;
  }
  const meta = source.metadata || source.data || {};
  const data = source.data || source.metadata || {};
  const persistedId = resolvePersistedNotificationId(
    source.notificationId,
    meta.notificationId,
    data.notificationId,
    source.id
  );
  const displayId = source.id ?? persistedId ?? source.notificationId ?? null;
  return {
    notificationId: persistedId,
    id: displayId,
    notificationType: source.notificationType || source.type || meta.notificationType || data.notificationType,
    type: source.type || source.notificationType,
    title: source.title,
    message: source.message ?? source.body,
    body: source.body ?? source.message,
    ticketId:
      source.ticketId ??
      meta.ticketId ??
      data.ticketId ??
      null,
    ticketNumber:
      source.ticketNumber ??
      meta.ticketNumber ??
      data.ticketNumber ??
      null,
    link: source.link ?? meta.link ?? data.link ?? null,
    chatSessionId:
      source.chatSessionId ??
      meta.chatSessionId ??
      data.chatSessionId ??
      meta.chat_session_id ??
      null,
    metadata: meta,
    data,
  };
}

/**
 * Register navigate + markAsRead from NotificationProvider (bell + toast share this).
 */
export function registerNotificationClickActions({ navigate = null, markAsRead = null } = {}) {
  notificationClickActions = { navigate, markAsRead };
}

/**
 * Same navigation path as NotificationBell click (single source of truth).
 * @param {object} notification
 * @param {unknown} [menuDataProp]
 * @returns {{ navigated: boolean, path: string | null, decision: string }}
 */
export function performNotificationClickNavigation(notification, menuDataProp) {
  if (!notification) {
    return { navigated: false, path: null, decision: 'no_notification' };
  }
  const payload = buildNotificationNavigationPayload(notification) || notification;
  const { path, decision } = resolveNotificationClickNavigation(payload, menuDataProp);
  if (!path || typeof notificationClickActions.navigate !== 'function') {
    return { navigated: false, path: path ?? null, decision };
  }
  let safePath = path;
  const menuData = loadMenuDataForNavigation(menuDataProp);
  if (isCurrentUserCustomer() && isAgentOnlyRoute(safePath)) {
    safePath = resolveCustomerPortalTicketsNavigation(payload, menuData).path;
  } else if (isCurrentUserTeamLead() && isAgentTicketDetailRoute(safePath)) {
    const nt = String(payload.notificationType || payload.type || '').toLowerCase();
    safePath = resolveTeamLeadTicketQueueNavigation(payload, menuData, nt).path;
  }
  if (typeof notificationClickActions.markAsRead === 'function') {
    notificationClickActions.markAsRead(payload);
  }
  notificationClickActions.navigate(safePath);
  const normalizedPath = normalizeRoute(safePath);
  if (
    isCurrentUserCustomer() &&
    (normalizedPath === CANONICAL_PORTAL_TICKETS ||
      normalizedPath.endsWith('/portal/tickets'))
  ) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CUSTOMER_PORTAL_TICKET_NOTIFICATION_EVENT));
    }
  }
  return { navigated: true, path: safePath, decision };
}
